import z from "zod";
import {
	designerTypes,
	architecteMissions,
	bureauEtudeMissions,
	controleurTechniqueMissions,
	geotechnicienMissions,
	maitreOeuvreMissions,
	builderMainActivities,
} from "@dune/vars";
import { useForm, useFormContext } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { DocumentRowDefinition } from "../../../../component/constructionSites/submission/participants/ReceptionDocumentsTable";

const commonParticipantToReceptionFieldsSchema = z.object({
	siren: z.string().optional(),
	denomination: z.string().min(1, "La dénomination est requise"),
	registrationNumber: z.string().optional(),
	country: z.string().optional(),
	streetNumber: z.string().optional(),
	streetName: z.string().optional(),
	postalCode: z.string().optional(),
	city: z.string().optional(),
	totalFeesTTC: z.coerce.number(),
	totalFeesHT: z.coerce.number(),
	invoiceFilesIds: z.array(z.string()),
});

const designerParticipantToReceptionFormSchema =
	commonParticipantToReceptionFieldsSchema
		.extend({
			designerType: z.enum(designerTypes),
			controleurTechniqueFinalReportsFilesIds: z.array(z.string()),
			decennialCertificatesFilesIds: z.array(z.string()),

			architecteMissions: z.array(z.enum(architecteMissions)).optional(),
			maitreOeuvreMissions: z.array(z.enum(maitreOeuvreMissions)).optional(),
			geotechnicienMissions: z.array(z.enum(geotechnicienMissions)).optional(),
			controleurTechniqueMissions: z
				.array(z.enum(controleurTechniqueMissions))
				.optional(),
			bureauEtudeMissions: z.array(z.enum(bureauEtudeMissions)).optional(),
			missions: z.undefined(),
		})
		.refine(
			(data) => {
				switch (data.designerType) {
					case "ARCHITECTE":
						return (
							data.architecteMissions && data.architecteMissions.length > 0
						);
					case "MAITRE_OEUVRE":
						return (
							data.maitreOeuvreMissions && data.maitreOeuvreMissions.length > 0
						);
					case "GEOTECHNICIEN":
						return (
							data.geotechnicienMissions &&
							data.geotechnicienMissions.length > 0
						);
					case "CONTROLEUR_TECHNIQUE":
						return (
							data.controleurTechniqueMissions &&
							data.controleurTechniqueMissions.length > 0
						);
					case "BUREAU_ETUDE":
						return (
							data.bureauEtudeMissions && data.bureauEtudeMissions.length > 0
						);
					default:
						return false;
				}
			},
			{
				message: "Sélectionner au moins une mission",
				path: ["missions"],
			},
		);

const builderParticipantToReceptionFormSchema =
	commonParticipantToReceptionFieldsSchema.extend({
		pvReceptionFilesIds: z.array(z.string()).optional(),
		decennialCertificatesFilesIds: z.array(z.string()).optional(),
		activities: z
			.array(z.enum(builderMainActivities))
			.min(1, "Au moins une activité doit être sélectionnée"),
	});

const materialParticipantToReceptionFormSchema = z.object({
	invoiceFilesIds: z.array(z.string()),
	materialTypes: z
		.array(z.string())
		.min(1, "Au moins un type de matériau doit être spécifié"),
	totalFeesTTC: z.coerce.number().min(0, "Le montant TTC doit être positif"),
	totalFeesHT: z.coerce.number().nullable().optional(),
});

const participantToReceptionFormSchema = z.union([
	designerParticipantToReceptionFormSchema,
	builderParticipantToReceptionFormSchema,
	materialParticipantToReceptionFormSchema,
]);

export type ParticipantToReceptionFormData = z.infer<
	typeof participantToReceptionFormSchema
>;
export type DesignerToReceptionFormData = z.infer<
	typeof designerParticipantToReceptionFormSchema
>;
export type BuilderToReceptionFormData = z.infer<
	typeof builderParticipantToReceptionFormSchema
>;
export type MaterialToReceptionFormData = z.infer<
	typeof materialParticipantToReceptionFormSchema
>;

type ParticipantToReceptionFormMode = "add" | "update";

export const getParticipantToReceptionType = (
	data: ParticipantToReceptionFormData,
): "DESIGNER" | "BUILDER" | "MATERIAL" => {
	if ("designerType" in data) return "DESIGNER";
	if ("activities" in data) return "BUILDER";
	return "MATERIAL";
};

interface UseDesignerToSubmissionFormProps {
	mode: ParticipantToReceptionFormMode;
	onSubmit: (data: DesignerToReceptionFormData) => void;
	constructionSiteId?: number;
	participantId?: number;
}

export const useDesignerToReceptionForm = ({
	onSubmit,
}: UseDesignerToSubmissionFormProps) => {
	const form = useForm({
		resolver: zodResolver(designerParticipantToReceptionFormSchema),
		mode: "onChange",
		defaultValues: {
			invoiceFilesIds: [],
			controleurTechniqueFinalReportsFilesIds: [],
			decennialCertificatesFilesIds: [],
			architecteMissions: [],
			maitreOeuvreMissions: [],
			geotechnicienMissions: [],
			controleurTechniqueMissions: [],
			bureauEtudeMissions: [],
			totalFeesHT: 0,
			totalFeesTTC: 0,
			city: "",
			country: "",
			registrationNumber: "",
			siren: "",
			denomination: "",
			streetName: "",
			streetNumber: "",
			postalCode: "",
			designerType: undefined,
			missions: undefined,
		},
	});

	const handleSubmit = form.handleSubmit(onSubmit);

	return {
		form,
		handleSubmit,
		isSubmitting: form.formState.isSubmitting,
		errors: form.formState.errors,
	};
};

interface UseBuilderToReceptionFormProps {
	mode: ParticipantToReceptionFormMode;
	onSubmit: (data: BuilderToReceptionFormData) => void;
}

export const useBuilderToReceptionForm = ({
	onSubmit,
}: UseBuilderToReceptionFormProps) => {
	const form = useForm({
		resolver: zodResolver(builderParticipantToReceptionFormSchema),
		mode: "onChange",
		defaultValues: {
			invoiceFilesIds: [],
			decennialCertificatesFilesIds: [],
			activities: [],
			pvReceptionFilesIds: [],
			totalFeesHT: 0,
			totalFeesTTC: 0,
			city: "",
			country: "",
			registrationNumber: "",
			siren: "",
			denomination: "",
			streetName: "",
			streetNumber: "",
			postalCode: "",
		},
	});

	const handleSubmit = form.handleSubmit(onSubmit);

	return {
		form,
		handleSubmit,
		isSubmitting: form.formState.isSubmitting,
		errors: form.formState.errors,
	};
};

interface UseMaterialToReceptionFormProps {
	mode: ParticipantToReceptionFormMode;
	onSubmit: (data: MaterialToReceptionFormData) => void;
}

export const useMaterialToReceptionForm = ({
	onSubmit,
}: UseMaterialToReceptionFormProps) => {
	const form = useForm({
		resolver: zodResolver(materialParticipantToReceptionFormSchema),
		mode: "onChange",
		defaultValues: {
			invoiceFilesIds: [],
			materialTypes: [],
			totalFeesHT: 0,
			totalFeesTTC: 0,
		},
	});

	const handleSubmit = form.handleSubmit(onSubmit);

	return {
		form,
		handleSubmit,
		isSubmitting: form.formState.isSubmitting,
		errors: form.formState.errors,
	};
};

export const useDesignerReceptionDocumentRows = (): DocumentRowDefinition[] => {
	const { watch, setValue } = useFormContext<DesignerToReceptionFormData>();
	const designerType = watch("designerType");

	return [
		{
			label: "Facture",
			fileIds: watch("invoiceFilesIds"),
			onAddFile: (fileId: string) =>
				setValue("invoiceFilesIds", [...watch("invoiceFilesIds"), fileId]),
		},
		...(designerType === "CONTROLEUR_TECHNIQUE"
			? [
					{
						label: "Rapport final de contrôle technique",
						fileIds: watch("controleurTechniqueFinalReportsFilesIds"),
						onAddFile: (fileId: string) =>
							setValue("controleurTechniqueFinalReportsFilesIds", [
								...watch("controleurTechniqueFinalReportsFilesIds"),
								fileId,
							]),
					},
				]
			: []),
		{
			label: "Attestation décennale",
			fileIds: watch("decennialCertificatesFilesIds"),
			onAddFile: (fileId: string) =>
				setValue("decennialCertificatesFilesIds", [
					...watch("decennialCertificatesFilesIds"),
					fileId,
				]),
		},
	];
};

export const useBuilderReceptionDocumentRows = (): DocumentRowDefinition[] => {
	const { watch, setValue } = useFormContext<BuilderToReceptionFormData>();

	return [
		{
			label: "Facture",
			fileIds: watch("invoiceFilesIds"),
			onAddFile: (fileId: string) =>
				setValue("invoiceFilesIds", [...watch("invoiceFilesIds"), fileId]),
		},
		{
			label: "PV de réception",
			fileIds: watch("pvReceptionFilesIds") ?? [],
			onAddFile: (fileId: string) =>
				setValue("pvReceptionFilesIds", [
					...(watch("pvReceptionFilesIds") ?? []),
					fileId,
				]),
		},
		{
			label: "Attestation décennale",
			fileIds: watch("decennialCertificatesFilesIds") ?? [],
			onAddFile: (fileId: string) =>
				setValue("decennialCertificatesFilesIds", [
					...(watch("decennialCertificatesFilesIds") ?? []),
					fileId,
				]),
		},
	];
};

export const useMaterialReceptionDocumentRows = (): DocumentRowDefinition[] => {
	const { watch, setValue } = useFormContext<MaterialToReceptionFormData>();

	return [
		{
			label: "Facture",
			fileIds: watch("invoiceFilesIds"),
			onAddFile: (fileId: string) =>
				setValue("invoiceFilesIds", [...watch("invoiceFilesIds"), fileId]),
		},
	];
};
